import React, { useState, useEffect } from "react";
import api from "../services/api";
import "./EvenementForm.css";

interface ThemeData {
  background: string;
  text: string;
  accent: string;
  button: string;
}

interface FormDataState {
  type: string;
  province: string;
  dateISO: string;
  heureISO: string;
  image: File | null;
  imageUrl: string;
  rsvp: boolean;
  theme: ThemeData;
}

interface ProvincesResponse {
  success: boolean;
  provinces: string[];
}

interface EvenementResponse {
  success: boolean;
  id: string;
  message?: string;
}

const EVENT_TYPES = [
  { value: "bapteme", label: "Baptême" },
  { value: "fete", label: "Fête / Réception" },
  { value: "conference", label: "Conférence" },
  { value: "autre", label: "Autre événement" }
];

const EvenementForm: React.FC = () => {
  const [provinces, setProvinces] = useState<string[]>([]);
  const [formData, setFormData] = useState<FormDataState>({
    type: "bapteme",
    province: "",
    dateISO: "",
    heureISO: "",
    image: null,
    imageUrl: "",
    rsvp: false,
    theme: {
      background: "#ffffff",
      text: "#000000",
      accent: "#c2a46d",
      button: "#c2a46d"
    }
  });

  useEffect(() => {
    fetchProvinces();
  }, []);

  const fetchProvinces = async () => {
    try {
      const response = await api.get<ProvincesResponse>("/admin/users/provinces");
      if (response.data.success) {
        setProvinces(response.data.provinces);
      }
    } catch (error) {
      console.error("Erreur chargement provinces:", error);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleThemeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      theme: { ...prev.theme, [name]: value }
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target;
    if (files && files[0]) {
      const file = files[0];
      const fileUrl = URL.createObjectURL(file);
      setFormData(prev => ({ 
        ...prev, 
        image: file,
        imageUrl: fileUrl
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.image) {
      alert("Veuillez sélectionner une image pour l'événement");
      return;
    }

    if (!formData.dateISO || !formData.heureISO || !formData.province) {
      alert("Veuillez remplir tous les champs obligatoires : date, heure et province");
      return;
    }

    const formDataToSend = new FormData();
    formDataToSend.append('type', formData.type);
    formDataToSend.append('province', formData.province);
    formDataToSend.append('dateISO', formData.dateISO);
    formDataToSend.append('heureISO', formData.heureISO);
    formDataToSend.append('rsvp', formData.rsvp.toString());
    formDataToSend.append('theme', JSON.stringify(formData.theme));
    formDataToSend.append('image', formData.image);

    try {
      const response = await api.post<EvenementResponse>("/evenements", formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      alert(`Événement créé avec succès !\nID: ${response.data.id}`);

      if (formData.imageUrl) {
        URL.revokeObjectURL(formData.imageUrl);
      }

      setFormData({
        type: "bapteme",
        province: "",
        dateISO: "",
        heureISO: "",
        image: null,
        imageUrl: "",
        rsvp: false,
        theme: {
          background: "#ffffff",
          text: "#000000",
          accent: "#c2a46d",
          button: "#c2a46d"
        }
      });

    } catch (err: any) {
      console.error("Erreur complète:", err);
      const errorMessage = err.response?.data?.message || err.message || "Erreur inconnue";
      alert(`Erreur : ${errorMessage}`);
    }
  };

  return (
    <div className="evenement-form-container">
      <h2>Créer un événement</h2>
      <form onSubmit={handleSubmit} className="evenement-form">
        
        <div className="form-section">
          <h3>Type d'événement</h3>
          <div className="form-group">
            <label>Choisissez le type *</label>
            <select 
              name="type" 
              value={formData.type} 
              onChange={handleChange}
              required
            >
              {EVENT_TYPES.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-section">
          <h3>Informations générales</h3>
          
          <div className="form-row">
            <div className="form-group">
              <label>Date *</label>
              <input
                type="date"
                name="dateISO"
                value={formData.dateISO}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label>Heure *</label>
              <input
                type="time"
                name="heureISO"
                value={formData.heureISO}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label>Province *</label>
            <select name="province" value={formData.province} onChange={handleChange} required>
              <option value="">Sélectionnez une province</option>
              {provinces.map(prov => (
                <option key={prov} value={prov}>{prov}</option>
              ))}
            </select>
          </div>

          <div className="form-group checkbox-group">
            <label>
              <input
                type="checkbox"
                name="rsvp"
                checked={formData.rsvp}
                onChange={handleChange}
              />
              Activer la confirmation de présence (RSVP)
            </label>
          </div>
        </div>

        <div className="form-section">
          <h3>Image de l'événement</h3>
          <p className="info-message">
            L'image doit contenir toutes les informations de l'événement (nom, lieu, etc.)
          </p>
          
          <div className="form-group">
            <label>Image *</label>
            <input 
              type="file" 
              name="image" 
              accept="image/*" 
              onChange={handleFileChange}
              required
            />
            <small className="help-text">
              Sélectionnez une image pour illustrer votre événement.
            </small>
            {formData.imageUrl && (
              <div className="image-preview">
                <img src={formData.imageUrl} alt="Aperçu" />
                <small>{formData.image?.name}</small>
              </div>
            )}
          </div>
        </div>

        <div className="form-section">
          <h3>Personnalisation des couleurs</h3>
          <p className="info-message">
            Choisissez les couleurs pour harmoniser l'affichage avec votre image.
          </p>
          
          <div className="form-row">
            <div className="form-group">
              <label>Couleur de fond</label>
              <input
                type="color"
                name="background"
                value={formData.theme.background}
                onChange={handleThemeChange}
              />
              <small>Fond de la page</small>
            </div>

            <div className="form-group">
              <label>Couleur du texte</label>
              <input
                type="color"
                name="text"
                value={formData.theme.text}
                onChange={handleThemeChange}
              />
              <small>Couleur des textes</small>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Couleur d'accent</label>
              <input
                type="color"
                name="accent"
                value={formData.theme.accent}
                onChange={handleThemeChange}
              />
              <small>Bordures, séparateurs</small>
            </div>

            <div className="form-group">
              <label>Couleur des boutons</label>
              <input
                type="color"
                name="button"
                value={formData.theme.button}
                onChange={handleThemeChange}
              />
              <small>Boutons d'action</small>
            </div>
          </div>

          <div className="theme-preview">
            <p>Aperçu :</p>
            <div 
              className="preview-box" 
              style={{ 
                backgroundColor: formData.theme.background,
                color: formData.theme.text,
                border: `1px solid ${formData.theme.accent}`
              }}
            >
              <span style={{ color: formData.theme.text }}>Texte exemple</span>
              <button 
                style={{ 
                  backgroundColor: formData.theme.button,
                  color: formData.theme.background
                }}
              >
                Bouton
              </button>
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" className="submit-btn">
            Créer l'événement
          </button>
          <button 
            type="button" 
            className="reset-btn"
            onClick={() => {
              if (window.confirm("Voulez-vous réinitialiser le formulaire ?")) {
                if (formData.imageUrl) {
                  URL.revokeObjectURL(formData.imageUrl);
                }
                
                setFormData({
                  type: "bapteme",
                  province: "",
                  dateISO: "",
                  heureISO: "",
                  image: null,
                  imageUrl: "",
                  rsvp: false,
                  theme: {
                    background: "#ffffff",
                    text: "#000000",
                    accent: "#c2a46d",
                    button: "#c2a46d"
                  }
                });
              }
            }}
          >
            Réinitialiser
          </button>
        </div>
      </form>
    </div>
  );
};

export default EvenementForm;